// LevelCelebration - Celebration modal shown when completing a level

import { useEffect } from 'react';
import { useTTS } from '../../hooks/useTTS';

const LevelCelebration = ({ data, onClose, onNextLevel, voiceEnabled = true, playerVoice = 'alice' }) => {
  const { speakAsPlayer, stop: stopTTS } = useTTS();
  
  useEffect(() => {
    if (data && voiceEnabled) {
      const msg = `Well done! Level ${data.level} complete! You earned ${data.credits_earned} coins!`;
      setTimeout(() => speakAsPlayer(msg, playerVoice), 500);
    }
    return () => stopTTS();
  }, [data]);
  
  if (!data) return null;
  
  const { level, stage, credits_earned, new_building, stage_name } = data;
  
  return (
    <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-[9999] p-4" style={{ isolation: 'isolate' }}>
      <div className="relative bg-white rounded-3xl p-8 max-w-md w-full text-center shadow-2xl animate-bounce-in border-4 border-sunny-yellow">
        {/* Confetti effect */}
        <div className="absolute inset-0 pointer-events-none overflow-hidden rounded-3xl">
          {[...Array(20)].map((_, i) => (
            <div
              key={i}
              className="absolute animate-confetti"
              style={{
                left: `${Math.random() * 100}%`,
                top: `-20px`,
                animationDelay: `${Math.random() * 2}s`,
                animationDuration: `${2 + Math.random() * 2}s`,
              }}
            >
              {['*', '+', '.', '~'][Math.floor(Math.random() * 4)]}
            </div>
          ))}
        </div>
        
        <h2 className="font-display text-3xl text-text-dark mb-2">
          Level {level} Complete!
        </h2>
        
        <p className="text-text-medium mb-4">
          Stage {stage}: {stage_name}
        </p>
        
        <div className="bg-sunny-yellow/30 rounded-2xl p-4 mb-4 border-2 border-sunny-yellow">
          <div className="flex items-center justify-center gap-2">
            <span className="font-display text-3xl text-sunny-yellow">+{credits_earned}</span>
            <span className="text-text-medium">coins earned!</span>
          </div>
        </div>
        
        {new_building && (
          <div className="bg-mint-green/30 rounded-2xl p-4 mb-4 border-2 border-mint-green">
            <p className="text-mint-green font-bold mb-2">NEW ITEM UNLOCKED!</p>
            <div className="text-5xl mb-2">{new_building.emoji}</div>
            <p className="font-display text-xl text-text-dark">{new_building.name}</p>
            <p className="text-sm text-text-medium">Available in Build Menu</p>
          </div>
        )}
        
        <div className="flex gap-3">
          <button
            onClick={() => { stopTTS(); onNextLevel ? onNextLevel() : onClose(); }}
            className="flex-1 py-4 bg-gradient-to-r from-sunny-yellow to-coral-orange hover:from-coral-orange hover:to-sunny-yellow rounded-2xl text-white font-display text-xl transition-all shadow-lg"
            data-testid="level-celebration-next"
          >
            Next Level!
          </button>
          <button
            onClick={() => { stopTTS(); onClose(); }}
            className="px-6 py-4 bg-soft-gray/50 hover:bg-soft-gray rounded-2xl text-text-medium font-display text-lg transition-all"
            data-testid="level-celebration-continue"
          >
            Explore
          </button>
        </div>
      </div>
      
      <style>{`
        @keyframes bounce-in {
          0% { transform: scale(0.3); opacity: 0; }
          50% { transform: scale(1.05); }
          70% { transform: scale(0.9); }
          100% { transform: scale(1); opacity: 1; }
        }
        .animate-bounce-in {
          animation: bounce-in 0.6s ease-out;
        }
        @keyframes confetti {
          0% { transform: translateY(-20px) rotate(0deg); opacity: 1; }
          100% { transform: translateY(400px) rotate(720deg); opacity: 0; }
        }
        .animate-confetti {
          animation: confetti 3s linear infinite;
        }
      `}</style>
    </div>
  );
};

export default LevelCelebration;
